import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useViewTransitionState } from "react-router";
import { getProductQueryOptions } from "@/shared/api/generated/catalog-controller/catalog-controller";
import type { ProductCardResponse } from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import placeholder from "@/shared/assets/product-placeholder.svg";
import { cn } from "@/shared/lib/cn";
import { formatSum } from "@/shared/lib/money";
import { Badge } from "../badge";
import { Badges } from "./Badges";

/**
 * Product tile (Figma "Card", D18): picture 1:1, up to two badges, the yellow monthly payment with its term first,
 * then the price with «сум», the name on two lines, and a stock line only when it matters ("Осталось мало", "Под
 * заказ"). The whole card is one link; `actions` is the slot in the picture's corner (the favorites heart). A touch
 * starts loading the product, so the page usually opens with its data; the picture then morphs into the gallery.
 */
export function ProductCard({
  product,
  actions,
}: {
  product: ProductCardResponse;
  actions?: ReactNode;
}) {
  const { t } = useTranslation(["common", "catalog"]);
  const href = `/product/${product.p_slug}`;
  const queryClient = useQueryClient();
  const transitioning = useViewTransitionState(href);
  const [loaded, setLoaded] = useState(false);
  const prefetch = () => {
    // warms the cache only; a failure shows up when the page itself loads
    queryClient
      .query({ ...getProductQueryOptions(product.p_slug), staleTime: 30_000 })
      .catch(() => undefined);
  };

  return (
    <article className="group relative flex h-full flex-col gap-2 rounded-card bg-surface p-2 pb-3 lg:gap-3 lg:bg-transparent lg:p-0">
      <div className="relative aspect-square overflow-hidden rounded-lg2 bg-surface lg:rounded-card lg:border lg:border-line">
        <img
          src={product.p_image_url ?? placeholder}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => {
            setLoaded(true);
          }}
          ref={(image) => {
            if (image?.complete) setLoaded(true);
          }}
          style={transitioning ? { viewTransitionName: "product-image" } : undefined}
          className={cn(
            "size-full object-contain p-3 transition-[opacity,scale] duration-(--motion-base) group-hover:scale-105 lg:p-6",
            product.p_image_url ? (loaded ? "opacity-100" : "opacity-0") : "object-cover p-0",
          )}
        />
        {actions && <div className="absolute top-1 right-1 z-10 flex">{actions}</div>}
        <Badges
          discountPercent={product.p_discount_percent}
          isNew={product.p_is_new}
          className="absolute bottom-2 left-2"
        />
      </div>

      <div className="flex flex-1 flex-col gap-1.5 px-1">
        <MonthlyBadge amount={product.p_monthly_payment} months={product.p_installment_months} />
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-title-xs font-semibold text-ink-strong tabular-nums">
            {t("catalog:sum", { amount: formatSum(product.p_price) })}
          </span>
          {product.p_old_price != null && (
            <s className="text-body-lg text-muted tabular-nums">
              <span className="sr-only">{t("product.oldPrice")} </span>
              {formatSum(product.p_old_price)}
            </s>
          )}
        </p>
        <h3 className="line-clamp-2 text-body-lg text-ink">
          {/* The whole card is clickable through this link's ::after overlay. */}
          <Link
            to={href}
            viewTransition
            onPointerDown={prefetch}
            onFocus={prefetch}
            className="after:absolute after:inset-0 after:rounded-card hover:text-brand-600"
          >
            {product.p_name}
          </Link>
        </h3>
        {product.p_stock_level !== "IN_STOCK" && (
          <p className="mt-auto flex items-center gap-1.5 text-body-md text-muted">
            <span
              aria-hidden
              className={cn(
                "size-1.5 rounded-full",
                product.p_stock_level === "FEW" ? "bg-warning" : "bg-dimmed",
              )}
            />
            {t(`catalog:stock.${product.p_stock_level}`)}
          </p>
        )}
      </div>
    </article>
  );
}

/**
 * The yellow monthly payment, always with its term (D14, D18): «499 167 сум × 12 мес». Yellow means the monthly
 * payment and nothing else.
 */
export function MonthlyBadge({
  amount,
  months,
  size = "md",
}: {
  amount: number;
  months: number;
  /** `lg`: the product page's buy block. */
  size?: "md" | "lg";
}) {
  const { t } = useTranslation();
  return (
    <Badge
      variant="price"
      tone="warning"
      className={cn(
        "h-auto w-fit flex-wrap gap-x-1 rounded-md py-0.5 font-semibold whitespace-normal tabular-nums",
        size === "lg" ? "px-2 py-1 text-title-md" : "px-1.5 text-body-lg",
      )}
    >
      <span>{t("product.perMonthAmount", { amount: formatSum(amount) })}</span>
      <span className="font-medium text-ink-toned">
        {t("product.termMonths", { count: months })}
      </span>
    </Badge>
  );
}
